import React, { useState, useEffect, useSyncExternalStore } from "react";
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  TableCellsIcon, // used by intro-note (commented out below)
} from "@heroicons/react/24/outline";
import Tooltip from "./components/Tooltip";

const mobileQuery = "(max-width: 639px)";
function subscribeMobile(cb: () => void) {
  const mql = window.matchMedia(mobileQuery);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}
function getSnapshotMobile() { return window.matchMedia(mobileQuery).matches; }
function getServerSnapshotMobile() { return false; }
function useIsMobile() {
  return useSyncExternalStore(subscribeMobile, getSnapshotMobile, getServerSnapshotMobile);
}

interface ConversationStartersProps {
  onSelectStarter: (prompt: string, id: string) => void;
  disabled?: boolean;
}

interface Starter {
  id: string;
  label: string;
  followUpPrompt: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  enabled: boolean;
  exampleQueries?: string[];
  tooltip?: string;
}

/**
 * Conversation starter pills component
 * Displays clickable options to help users start conversations
 */
export default function ConversationStarters_Component({
  onSelectStarter,
  disabled = false,
}: ConversationStartersProps): JSX.Element {
  const [currentExampleIndex, setCurrentExampleIndex] = useState<Record<string, number>>({});
  const isMobile = useIsMobile();

  const starters: Starter[] = [
    {
      id: "past-deals",
      label: "Find Deals & Ask Questions",
      followUpPrompt:
        "What type of deals are you interested in? You can search by company name, industry, deal type, or any other criteria.",
      icon: MagnifyingGlassIcon,
      enabled: true,
      exampleQueries: [
        "What telco companies have we looked at recently?",
        "Find CVC deals with preferred/structured equity",
        "I'm looking at Six Nations. Find relevant past deals",
        "What are typical IC concerns on recent tech deals?",
      ],
      tooltip:
        "Search and interrogate CVC's library of past deals, published IC papers and DD docs. Only contains PE deals that reached PIR after 2011. Other strategies coming soon",
    },
    {
      id: "offer-letter",
      label: "Draft Offer Letter",
      followUpPrompt:  `I can help you create an offer letter. What company or deal would you like to create an offer letter for? To get started, you can upload:
1) The process letter
2) Any example offer letters
3) Supplementary documents (notes, pre-PIRs, etc.)

Supported formats are pdf, docx, and markdown.

Please let me know once you've uploaded all files.`,
      icon: DocumentTextIcon,
      enabled: true,
      exampleQueries: [
        "Draft me an NBO for Jagex",
        "Here's ACorp's process letter, write an offer letter",
      ],
      tooltip:
        "Write and edit an offer letter for a target company based on input documents like process letter, Pre-PIR, CIM, and previous offer letters from your team. Outputs in CVC formatted docx, ready to go.",
    },
    // {
    //   id: "intro-note",
    //   label: "Draft Intro Note",
    //   followUpPrompt: `I can help you create a deal introduction note for the private credit team. To get started, please upload:
    // 1) Deal documents (CIMs, financial summaries, deal memos, lender presentations)
    // 2) Any example intro notes from previous deals
    // 3) Supplementary materials (market research, comparable analyses, etc.)
    //
    // Supported formats are pdf, docx, and markdown.
    //
    // Please also share any key deal parameters (project name, company name, timing) if not in the documents.`,
    //   icon: TableCellsIcon,
    //   enabled: false,
    //   exampleQueries: [
    //     "Draft an intro note for Project Aurora",
    //     "Create a credit intro note for LexFlow Systems",
    //   ],
    //   tooltip:
    //     "Generate deal introduction notes for the private credit team. Upload deal documents and financial data to create a structured one-page investment summary.",
    // },
  ];

  // Rotate through example queries every 3 seconds for each card
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    starters.forEach((starter) => {
      if (starter.exampleQueries && starter.exampleQueries.length > 0) {
        const interval = setInterval(() => {
          setCurrentExampleIndex((prev) => ({
            ...prev,
            [starter.id]: ((prev[starter.id] || 0) + 1) % starter.exampleQueries!.length,
          }));
        }, 3000);
        intervals.push(interval);
      }
    });

    return () => intervals.forEach((interval) => clearInterval(interval));
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-2 sm:py-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {starters.map((starterDef) => {
          const mobileDisabled = isMobile && starterDef.id === 'offer-letter';
          const starter = mobileDisabled ? { ...starterDef, enabled: false } : starterDef;
          const Icon = starter.icon;
          return (
            <Tooltip
              key={starter.id}
              content={starter.tooltip || ""}
              disabled={disabled || !starter.enabled}
            >
              <button
                onClick={() =>
                  !disabled &&
                  starter.enabled &&
                  onSelectStarter(starter.followUpPrompt, starter.id)
                }
                disabled={disabled || !starter.enabled}
                className={`relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 text-left min-h-[120px] w-full ${
                  disabled || !starter.enabled
                    ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-50"
                    : "border-gray-200 bg-white hover:border-blue-500 hover:shadow-md cursor-pointer"
                }`}
              >
                {starter.enabled && (
                  <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    BETA
                  </span>
                )}
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    disabled || !starter.enabled ? "bg-gray-200" : "bg-blue-100"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      disabled || !starter.enabled
                        ? "text-gray-400"
                        : "text-blue-600"
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-sm tablet:text-[18px] tablet:leading-[20px] font-medium ${
                      disabled || !starter.enabled
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {starter.label}
                  </div>
                  {!starter.enabled ? (
                    <div className="text-xs text-gray-400 mt-0.5 min-h-[2.5rem]">
                      {mobileDisabled ? "Not available on mobile yet!" : "Coming soon"}
                    </div>
                  ) : starter.exampleQueries &&
                    starter.exampleQueries.length > 0 ? (
                    <div
                      key={currentExampleIndex[starter.id] || 0}
                      className="text-xs text-gray-500 mt-1 italic animate-fade-in min-h-[2.5rem] flex items-start"
                    >
                      "{starter.exampleQueries[currentExampleIndex[starter.id] || 0]}"
                    </div>
                  ) : null}
                </div>
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
